// Copyright (c) Bentley Systems
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  checkInternalServerError,
  getParametersFromUrlString,
  getTranslatedStringFromParameters,
} from "../../Helpers/helper-functions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAccessToken } from "../../app/authorization-slice";
import { Alert, Button, LabeledTextarea, ProgressRadial, Text } from "@itwin/itwinui-react";
import { setConfirmation } from "../../app/confirmation-slice";
import LabeledInputComponent from "../../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../../Helpers/enumerators";
import { OnPremise } from "../CredentialsList/Models/on-premise";
import { SaveOnPremiseDto } from "./save-on-premise-dto";
import { useToastNotificationService } from "../../Helpers/ToastNotificationService";
import { ConfigManager } from "../../Helpers/config-manager";

export type OnPremFormProps = {
  params?: string;
  iTwinId: string;
  onPrem?: OnPremise;
  ///is open directly from URL or is it open in InformationPanel in elist
  isFromUrl: boolean;
  isStandaloneMode?: boolean;
  closeFrollFunction?: (refreshList: boolean) => void;
  navigateToInformationPanelFunction?: (navFrolUrl: string) => void;
  setInforPanelHeaderFunction?: (header: string) => void;
};

const OnPremForm = (props: OnPremFormProps) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToastNotificationService();
  const { params } = useParams();
  const urlParameters = useMemo(
    () => getParametersFromUrlString(params),
    [params],
  );
  const [isLoading, setLoading] = useState<boolean>(false);

  const parameters =
    props && props.isFromUrl
      ? urlParameters
      : getParametersFromUrlString(props.params);

  const accessToken = useAppSelector(selectAccessToken);
  const [name, setName] = useState<string>();
  const [mqttClientId, setmqttClientId] = useState<string>();
  const [mqttConnectionKey, setMqttConnectionKey] = useState<string>();
  const [mqttEncryptionKey, setMqttEncryptionKey] = useState<string>();
  const [notificationEmail, setNotificationEmail] = useState<string>();
  const [notes, setNotes] = useState<string>();

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isFormDataChanged, setIsFormDataChanged] = useState<boolean>(false);

  const [statuses, setStatuses] = useState<{
    [key: string]: "positive" | "warning" | undefined;
  }>({});

  const handleStatusChange = (
    id: string,
    status: "positive" | "warning" | undefined,
  ) => {
    setStatuses((prevStatuses) => {
      if (prevStatuses[id] !== status) {
        return {
          ...prevStatuses,
          [id]: status,
        };
      }
      return prevStatuses;
    });
  };

  const allInputStatusesValid = Object.values(statuses).every(
    (status) => status !== "warning",
  );

  const setInfoPanelHeader = useCallback(
    (vmHeader: string | undefined) => {
      if (
        props.setInforPanelHeaderFunction !== undefined &&
        vmHeader !== undefined
      ) {
        const header = getTranslatedStringFromParameters(vmHeader, parameters);
        props.setInforPanelHeaderFunction(header as string);
      }
    },
    [parameters, props],
  );

  useEffect(() => {
    const headerStr = `${props.onPrem === undefined ? "New" : "Edit"} on premise client`;
    setInfoPanelHeader(headerStr);
  }, [props.onPrem, setInfoPanelHeader]);

  useMemo(() => {
    // Connection/encryption keys are only mandatory when creating a new client; on update they
    // may be left blank to keep the existing value (PATCH semantics).
    const isUpdateMode = props.onPrem !== undefined;
    const keysValid = isUpdateMode || (mqttConnectionKey && mqttEncryptionKey);
    if (name && mqttClientId && notificationEmail && keysValid) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [
    name,
    mqttClientId,
    mqttConnectionKey,
    mqttEncryptionKey,
    notificationEmail,
    props.onPrem,
  ]);

  const wasFormChanged = useCallback(() => {
    if (props.onPrem !== undefined) {
      if (
        props.onPrem.name === name &&
        props.onPrem.mqttClientId === mqttClientId &&
        props.onPrem.notificationEmail === notificationEmail &&
        (props.onPrem.notes ?? "") === (notes ?? "") &&
        !mqttConnectionKey &&
        !mqttEncryptionKey
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      if (
        name ||
        mqttClientId ||
        mqttConnectionKey ||
        mqttEncryptionKey ||
        notificationEmail ||
        notes
      ) {
        return true;
      } else {
        return false;
      }
    }
  }, [
    name,
    mqttClientId,
    mqttConnectionKey,
    mqttEncryptionKey,
    notificationEmail,
    notes,
    props.onPrem,
  ]);

  useMemo(() => {
    const isFormChanged = wasFormChanged();
    setIsFormDataChanged(isFormChanged);
  }, [wasFormChanged]);

  useEffect(() => {
    setName(props.onPrem?.name);
    setmqttClientId(props.onPrem?.mqttClientId);
    setNotificationEmail(props.onPrem?.notificationEmail);
    setNotes(props.onPrem?.notes);
  }, [props.onPrem, dispatch]);

  function getSaveJson() {
    const saveOnPremiseDto: SaveOnPremiseDto = {
      name: name as string,
      mqttClientId: mqttClientId as string,
      notificationEmail: notificationEmail as string,
      notes,
    };
    // Omit empty key fields entirely rather than sending "" so the update keeps the existing
    // connection/encryption key untouched.
    if (mqttConnectionKey) {
      saveOnPremiseDto.mqttConnectionKey = mqttConnectionKey;
    }
    if (mqttEncryptionKey) {
      saveOnPremiseDto.mqttEncryptionKey = mqttEncryptionKey;
    }
    const jsonBody = JSON.stringify(saveOnPremiseDto);

    return jsonBody;
  }

  const saveOnPrem = async () => {
    setLoading(true);
    let path = `/itwins/${props.iTwinId}/onpremises`;
    if (isUpdate) {
      path += `/${props.onPrem?.id}`;
    }
    const saveUrl = ConfigManager.edfsItwinInternalUrl + path;
    const jsonBody = getSaveJson();
    //let responseStatus = 0;
    const saveRunId = crypto.randomUUID();
    const fetchResponse = await fetch(saveUrl, {
      method: isUpdate ? "PUT" : "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: saveRunId,
      },
      body: jsonBody,
    }).then(async function (response) {
      checkInternalServerError(response.status, dispatch, saveRunId);
      return response.json();
    });
    setLoading(false);
    if (fetchResponse.error) {
      showToast({
        status: "negative",
        message: `${fetchResponse.error.message} ActivityId="${saveRunId}"`,
      });
    } else {
      showToast({ status: "positive", action: isUpdate ? "update" : "create" });
      closeFRoll(true);
    }
  };

  const onCancelClick = () => {
    if (isFormDataChanged) {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: () => closeFRoll(false),
          caption: "Discard unsaved changes?",
          text: "Changes you made so far will not be updated",
          okText: "Discard anyway",
          cancelText: "Stay",
        }),
      );
    } else {
      closeFRoll(false);
    }
  };

  const closeFRoll = (refreshList: boolean) => {
    if (props.closeFrollFunction !== undefined) {
      props.closeFrollFunction(refreshList);
    }
  };

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    void saveOnPrem();
  };

  const isUpdate = props.onPrem !== undefined;
  return (
    <div
      style={{ height: "100%", overflowY: "hidden" }}
      className="iuiBackgroundColor"
    >
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div style={{ height: "100%" }}>
          {props.isFromUrl && (
            <div className="becs-all-margin">
              <div className="rowWrap">
                <Text as="h3" variant="title">
                  New on premise client
                </Text>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} style={{ height: "100%" }}>
            <div
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="becs-infopanelbody-footer-line"
                style={{
                  flex: 1,
                  overflowY: "auto",
                }}
              >
                <div className="becs-all-margin">
                  <LabeledInputComponent
                    required={true}
                    validationType={EfFieldValidationTypeEnum.Name100}
                    onStatusChange={(status) =>
                      handleStatusChange("name", status)
                    }
                    displayStyle="default"
                    label="Name"
                    placeholder="Enter text here..."
                    maxLength={100}
                    key="LbInConnName"
                    defaultValue={name}
                    onChange={(currentValue) => {
                      setName(currentValue.target.value);
                    }}
                  />

                  <div className="becs-t-margin">
                    <LabeledInputComponent
                      required={true}
                      validationType={EfFieldValidationTypeEnum.MqttClientId}
                      onStatusChange={(status) =>
                        handleStatusChange("mqttClientId", status)
                      }
                      displayStyle="default"
                      label="Mqtt client id"
                      placeholder="Enter text here..."
                      maxLength={250}
                      key="LbInMqttClientId"
                      defaultValue={mqttClientId}
                      onChange={(currentValue) => {
                        setmqttClientId(currentValue.target.value);
                      }}
                    />
                  </div>
                  <div className="becs-t-margin">
                    <LabeledInputComponent
                      required={true}
                      validationType={EfFieldValidationTypeEnum.Email}
                      onStatusChange={(status) =>
                        handleStatusChange("notificationEmail", status)
                      }
                      displayStyle="default"
                      label="Notification email"
                      placeholder="Enter text here..."
                      maxLength={256}
                      key="LbInNotificationEmail"
                      defaultValue={notificationEmail}
                      onChange={(currentValue) => {
                        setNotificationEmail(currentValue.target.value);
                      }}
                    />
                  </div>
                  {isUpdate && (
                    <div className="becs-tdouble-margin">
                      <Alert>
                        For your privacy and safety, the connection key and
                        encryption key are never shown. Leave a key field
                        blank to keep its current value, or enter a new value
                        to replace it.
                      </Alert>
                    </div>
                  )}
                  <div className="becs-t-margin">
                    <LabeledInputComponent
                      required={!isUpdate}
                      type="password"
                      validationType={EfFieldValidationTypeEnum.MqttKey}
                      onStatusChange={(status) =>
                        handleStatusChange("mqttConnectionKey", status)
                      }
                      displayStyle="default"
                      label="Mqtt connection key"
                      placeholder={isUpdate ? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" : "Enter text here..."}
                      maxLength={250}
                      key="LbInMqttConnKey"
                      defaultValue={mqttConnectionKey}
                      onChange={(currentValue) => {
                        setMqttConnectionKey(currentValue.target.value);
                      }}
                    />
                    {isUpdate && !mqttConnectionKey && (
                      <Text variant="small" isMuted>
                        A key is already set. Leave blank to keep it, or
                        enter a new value to replace it.
                      </Text>
                    )}
                  </div>
                  <div className="becs-t-margin">
                    <LabeledInputComponent
                      required={!isUpdate}
                      type="password"
                      validationType={EfFieldValidationTypeEnum.MqttKey}
                      onStatusChange={(status) =>
                        handleStatusChange("mqttEncryptionKey", status)
                      }
                      displayStyle="default"
                      label="Mqtt encryption key"
                      placeholder={isUpdate ? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" : "Enter text here..."}
                      maxLength={250}
                      key="LbInMqttEncrKey"
                      defaultValue={mqttEncryptionKey}
                      onChange={(currentValue) => {
                        setMqttEncryptionKey(currentValue.target.value);
                      }}
                    />
                    {isUpdate && !mqttEncryptionKey && (
                      <Text variant="small" isMuted>
                        A key is already set. Leave blank to keep it, or
                        enter a new value to replace it.
                      </Text>
                    )}
                  </div>
                  <div className="becs-t-margin">
                    <LabeledTextarea
                      label="Notes"
                      placeholder="Enter text here..."
                      maxLength={2000}
                      key="LbInNotes"
                      defaultValue={notes}
                      onChange={(currentValue) => {
                        setNotes(currentValue.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div className="becs-all-margin">
                  <div className="rowWrap">
                    <Button
                      key="saveCredentialBtn"
                      as="button"
                      styleType="cta"
                      type="submit"
                      disabled={
                        !(
                          isFormValid &&
                          isFormDataChanged &&
                          allInputStatusesValid
                        )
                      }
                    >
                      <div>
                        {props.onPrem === undefined ? "Save" : "Update"}
                      </div>
                    </Button>
                    <Button
                      key="cancelCredentialBtn"
                      as="button"
                      onClick={() => onCancelClick()}
                    >
                      <div>Cancel</div>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default OnPremForm;
